import { create } from 'zustand'
import { showErrorToast } from '@/components/ui/custom-toasts'
import { useSmokeballSdkStore } from './use-smokeball-sdk-store'
import { getMatter } from '@/actions/get-matter'

type Matter = {
  id: string
  number: string
  description: string
  matterTypeId?: string
}

type AccountMatterStore = {
  accountId: string | undefined;
  matterId: string | undefined;
  matter: Matter | null;

  setAccountId: (accountId: AccountMatterStore["accountId"]) => void;
  setMatterId: (matterId: AccountMatterStore["matterId"]) => void;
}

export const useAccountMatterStore = create<AccountMatterStore>((set, get) => ({
  accountId: undefined,
  matterId: undefined,
  matter: null,

  setAccountId: (accountId) => {
    if (get().accountId === accountId) return;
    set({ accountId });
    fetchMatter();
  },
  setMatterId: (matterId) => {
    if (get().matterId === matterId) return;
    set({ matterId });
    fetchMatter();
  }
}));

async function fetchMatter() {
  const isSdkLoading = useSmokeballSdkStore.getState().isLoading;

  if (isSdkLoading) {
    return; // don't know if we are using the sdk or api yet
  }

  const matterId = useAccountMatterStore.getState().matterId;
  const accountId = useAccountMatterStore.getState().accountId;
  if (!matterId || !accountId) {
    useAccountMatterStore.setState({ matter: null });
    return;
  }

  const isSdkAvailable = useSmokeballSdkStore.getState().isAvailable;
  const sdk = useSmokeballSdkStore.getState().sdk;

  if (!!isSdkAvailable && !!sdk) {
    const sdkMatter = await sdk.matters.get(matterId)
    useAccountMatterStore.setState({
      matter: {
        id: sdkMatter.id,
        number: sdkMatter.number,
        description: sdkMatter.description,
        matterTypeId: sdkMatter.matterTypeId,
      }
    })
  } else {
    const matterData = await getMatter({ accountId, matterId });
    if (!matterData || !matterData.data || !matterData.data.id) {
      console.error('Error fetching matter:', matterData)
              showErrorToast(`Something went wrong getting info about the current matter, if you need to use prompt templates please click the refresh button.`)
    } else {
      useAccountMatterStore.setState({
        matter: {
          id: matterData.data.id,
          number: matterData.data.number || '',
          description: matterData.data.description || '',
          matterTypeId: matterData.data.matterType?.id || undefined,
        }
      })
    }
  }
}

const subscribeToSdk = () => {
  const unsubscribe = useSmokeballSdkStore.subscribe(() => fetchMatter());

  // Return cleanup function
  return () => {
    // Unsubscribe from the store
    unsubscribe();
  };
}

export const initAccountMatterStore = () => {
  const disableSdkSub = subscribeToSdk();
  fetchMatter();

  return [disableSdkSub];
}


